/**
 * Batches (Suri, 2026-10-16): for the few logins ticked "Release batches" on the admin Operators page.
 *   - hand a locked batch to a picker (it then shows under "Your batches" on their phone),
 *   - mark a WHOLE batch as picked (for example it was picked from the paper sheet), crediting a chosen person.
 * The Worker re-checks the permission on every call; this screen just stays out of the way for everyone else.
 */
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, qs } from '../dom.js';
import { signal } from '../feedback.js';
import type { ReleaseBatch, ReleaseList } from '../types.js';

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Could not reach the server — check your connection.';
  if (err instanceof ApiError) return err.message;
  return String(err);
}

export const leftOf = (b: ReleaseBatch): number => Math.max(0, b.total - b.picked - b.short);

export async function renderRelease(container: HTMLElement): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  container.appendChild(div);
  await show(div);
}

async function show(div: HTMLElement, notice?: { kind: 'good' | 'bad'; text: string }, showAll = false): Promise<void> {
  div.innerHTML = `
    <div class="topbar"><h1>Batches</h1><div class="actions-row"><button class="ghost" id="back">Home</button><button class="ghost" id="refresh">Refresh</button></div></div>
    ${notice ? `<div class="card ${notice.kind === 'good' ? 'good' : 'bad'}"><p>${escapeHtml(notice.text)}</p></div>` : ''}
    <div id="body"><p class="hint center">Reading the batches…</p></div>`;
  qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => {
    location.hash = '#/';
  });
  qs<HTMLButtonElement>(div, '#refresh').addEventListener('click', () => void show(div, undefined, showAll));
  const body = qs<HTMLElement>(div, '#body');
  let list: ReleaseList;
  try {
    list = await api.releaseList();
  } catch (err) {
    body.innerHTML = `<div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p></div>`;
    return;
  }
  const rows = showAll ? list.batches : list.batches.filter((b) => leftOf(b) > 0);
  const shorts = list.shorts ?? [];
  body.innerHTML = `
    ${shorts.length > 0 ? `<div class="card release-shorts"><strong>${shorts.length} short order${shorts.length === 1 ? '' : 's'}</strong> — a picker could not find an item. Fix the stock, then put the order back in its batch.
      ${shorts.map((s, i) => `<div class="release-short"><div><strong>${escapeHtml(s.orderNumber)}</strong> · ${escapeHtml(s.batchName)}<div class="hint">${escapeHtml(s.sku)}${s.qty > 1 ? ` ×${s.qty}` : ''} · ${escapeHtml(s.location)} · ${escapeHtml(s.operatorName)}${s.note ? ` · ${escapeHtml(s.note)}` : ''}</div></div><button type="button" class="ghost reopen" data-i="${i}">Put back</button></div>`).join('')}</div>` : ''}
    <label class="release-toggle"><input type="checkbox" id="all" ${showAll ? 'checked' : ''}> Show finished batches too</label>
    ${rows.length === 0 ? '<div class="card center"><p><strong>No unfinished batches.</strong></p></div>' : '<div class="list" id="rows"></div>'}`;
  qs<HTMLInputElement>(div, '#all').addEventListener('change', (ev) => void show(div, undefined, (ev.target as HTMLInputElement).checked));
  body.querySelectorAll<HTMLButtonElement>('button.reopen').forEach((btn) => {
    btn.addEventListener('click', () => {
      const s = shorts[Number(btn.dataset.i)];
      if (!s) return;
      void (async () => {
        try {
          const r = await api.releaseReopenShort({ batchId: s.batchId, orderId: s.orderId, orderNumber: s.orderNumber });
          signal('good');
          await show(div, { kind: 'good', text: r.message }, showAll);
        } catch (err) {
          signal('bad');
          await show(div, { kind: 'bad', text: describeError(err) }, showAll);
        }
      })();
    });
  });
  const holder = body.querySelector<HTMLElement>('#rows');
  if (!holder) return;
  const userOptions = (sel: string, blank: string): string => `<option value="">${blank}</option>${list.users.map((u) => `<option value="${escapeHtml(u.id)}"${u.id === sel ? ' selected' : ''}>${escapeHtml(u.name)}</option>`).join('')}`;
  for (const b of rows) {
    const left = leftOf(b);
    const card = document.createElement('div');
    card.className = 'card release-card';
    card.innerHTML = `
      <div class="release-name">${escapeHtml(b.name)}</div>
      <div class="hint">${escapeHtml(b.runDate)} · ${escapeHtml(b.runLabel)} · <strong>${b.picked} / ${b.total}</strong> picked${b.short > 0 ? ` · <span class="error-text">${b.short} short</span>` : ''}</div>
      <div class="hint">Given to: <strong>${b.assignedUserName ? escapeHtml(b.assignedUserName) : 'nobody yet'}</strong></div>
      <div class="release-row"><select class="who">${userOptions(b.assignedUserId, '- nobody -')}</select><button type="button" class="ghost give">Give to picker</button></div>
      ${left > 0 ? `<details class="release-mark"><summary>Mark all ${left} as picked…</summary>
        <select class="credit">${userOptions(b.assignedUserId, 'Credit to…')}</select>
        <input class="why" placeholder="Why (e.g. picked from paper sheet)" maxlength="200" autocomplete="off">
        <button type="button" class="primary mark">Mark all ${left} picked</button></details>` : '<div class="hint">✓ All picked</div>'}`;
    holder.appendChild(card);
    card.querySelector<HTMLButtonElement>('.give')!.addEventListener('click', () => {
      const userId = qs<HTMLSelectElement>(card, '.who').value;
      void (async () => {
        try {
          const r = await api.releaseAssign({ runId: b.runId, batchId: b.batchId, userId });
          signal('good');
          await show(div, { kind: 'good', text: r.message }, showAll);
        } catch (err) {
          signal('bad');
          await show(div, { kind: 'bad', text: describeError(err) }, showAll);
        }
      })();
    });
    card.querySelector<HTMLButtonElement>('.mark')?.addEventListener('click', () => {
      const creditUserId = qs<HTMLSelectElement>(card, '.credit').value;
      const reason = qs<HTMLInputElement>(card, '.why').value.trim();
      if (creditUserId === '') return void window.alert('Choose who gets the credit for these picks.');
      if (reason.length < 3) return void window.alert('Type a reason (it is kept in the audit list).');
      const who = list.users.find((u) => u.id === creditUserId)?.name ?? '';
      if (!window.confirm(`Mark all ${left} remaining orders of ${b.name} as picked and credit them to ${who}?`)) return;
      void (async () => {
        try {
          const r = await api.releaseMarkPicked({ runId: b.runId, batchId: b.batchId, creditUserId, reason });
          signal('good');
          await show(div, { kind: 'good', text: r.message }, showAll);
        } catch (err) {
          signal('bad');
          await show(div, { kind: 'bad', text: describeError(err) }, showAll);
        }
      })();
    });
  }
}
